import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Box, Typography, Paper, List, ListItemButton, TextField,
  IconButton, Stack, CircularProgress, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, Chip, Avatar,
  Autocomplete, Divider, Tooltip, Badge,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import { fetchJson } from "./api";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

// ── helpers ──────────────────────────────────────────────────────────────────

function displayName(c) {
  if (c.is_group) return c.name || `Gruppo #${c.id}`;
  return (
    [c.other_nome, c.other_cognome].filter(Boolean).join(" ").trim() ||
    c.other_email ||
    `User #${c.other_user_id}`
  );
}

function timeLabel(iso, lang = "it") {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  if (sameDay)
    return d.toLocaleTimeString(lang === "en" ? "en-GB" : "it-IT", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString(lang === "en" ? "en-GB" : "it-IT", { day: "2-digit", month: "2-digit" });
}

// ── CreateGroupDialog ─────────────────────────────────────────────────────────

function CreateGroupDialog({ open, onClose, onCreated }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [productionId, setProductionId] = useState(null);
  const [productions, setProductions] = useState([]);
  const [memberQuery, setMemberQuery] = useState("");
  const [memberOptions, setMemberOptions] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);

  // carica produzioni per collegamento opzionale
  useEffect(() => {
    if (!open) return;
    fetchJson("productions").then((d) => {
      const list = Array.isArray(d) ? d : d?.productions ?? [];
      setProductions(list);
    }).catch(() => {});
  }, [open]);

  // ricerca utenti
  useEffect(() => {
    if (memberQuery.length < 2) { setMemberOptions([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetchJson(`messages/users/search?q=${encodeURIComponent(memberQuery)}`);
        setMemberOptions(res?.users ?? []);
      } catch { setMemberOptions([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [memberQuery]);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      const res = await fetchJson("messages/groups", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          memberIds: members.map((m) => m.id),
          productionId: productionId || undefined,
        }),
      });
      onCreated(res.id);
      handleClose();
    } catch (e) {
      console.error("[CREATE_GROUP]", e);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName(""); setProductionId(null);
    setMembers([]); setMemberQuery("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
        {t("messages.create_group_title")}
        <IconButton onClick={handleClose} size="small"
          sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          {/* nome gruppo */}
          <TextField
            label={t("messages.group_name_label")}
            fullWidth size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("messages.group_name_placeholder")}
            autoFocus
          />

          {/* produzione collegata (opzionale) */}
          {productions.length > 0 && (
          <Autocomplete
            options={productions}
            getOptionLabel={(o) => o.name || `Produzione #${o.id}`}
            value={productions.find((p) => p.id === productionId) || null}
            onChange={(_, v) => setProductionId(v?.id || null)}
            size="small"
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                {o.name || `Produzione #${o.id}`}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label={t("messages.production_label")} />
            )}
          />
        )}

          {/* aggiunta membri */}
          <Box>
            <Autocomplete
              options={memberOptions.filter((o) => !members.find((m) => m.id === o.id))}
              getOptionLabel={(o) =>
                [o.nome, o.cognome].filter(Boolean).join(" ") || o.email
              }
              inputValue={memberQuery}
              onInputChange={(_, v) => setMemberQuery(v)}
              onChange={(_, v) => {
                if (v) { setMembers((prev) => [...prev, v]); setMemberQuery(""); }
              }}
              loading={searching}
              size="small"
              filterOptions={(x) => x}
              noOptionsText={memberQuery.length < 2 ? t("messages.search_min_chars") : t("messages.no_results")}
              renderInput={(params) => (
                <TextField {...params} label={t("messages.add_members_label")} placeholder={t("messages.search_placeholder")} />
              )}
            />

            {members.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.25 }}>
                {members.map((m) => (
                  <Chip
                    key={m.id}
                    size="small"
                    label={[m.nome, m.cognome].filter(Boolean).join(" ") || m.email}
                    onDelete={() => setMembers((prev) => prev.filter((x) => x.id !== m.id))}
                    avatar={<Avatar sx={{ width: 20, height: 20, fontSize: 10 }}>
                      {(m.nome?.[0] || m.email?.[0] || "?").toUpperCase()}
                    </Avatar>}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} variant="outlined"
          sx={{ borderRadius: 20, textTransform: "none" }}>
          {t("messages.cancel")}
        </Button>
        <Button onClick={handleCreate} variant="contained" disabled={!name.trim() || loading}
          sx={{ borderRadius: 20, textTransform: "none", fontWeight: 700 }}>
          {loading ? <CircularProgress size={18} /> : t("messages.create_group")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── AddMemberDialog ───────────────────────────────────────────────────────────

function AddMemberDialog({ open, conversationId, onClose }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (query.length < 2) { setOptions([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetchJson(`messages/users/search?q=${encodeURIComponent(query)}`);
        setOptions(res?.users ?? []);
      } catch { setOptions([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const handleAdd = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      await fetchJson(`messages/groups/${conversationId}/members`, {
        method: "POST",
        body: JSON.stringify({ userId: selected.id }),
      });
      onClose(true);
      setSelected(null); setQuery("");
    } catch (e) {
      console.error("[ADD_MEMBER]", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800 }}>{t("messages.add_member_title")}</DialogTitle>
      <DialogContent>
        <Autocomplete
          options={options}
          getOptionLabel={(o) => [o.nome, o.cognome].filter(Boolean).join(" ") || o.email}
          inputValue={query}
          onInputChange={(_, v) => setQuery(v)}
          onChange={(_, v) => setSelected(v)}
          loading={searching}
          size="small"
          filterOptions={(x) => x}
          noOptionsText={query.length < 2 ? "Digita almeno 2 caratteri" : "Nessun risultato"}
          sx={{ mt: 1 }}
          renderInput={(params) => (
            <TextField {...params} label="Cerca utente" autoFocus />
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={() => onClose(false)} variant="outlined"
          sx={{ borderRadius: 20, textTransform: "none" }}>{t("messages.cancel")}</Button>
        <Button onClick={handleAdd} variant="contained"
          disabled={!selected || loading}
          sx={{ borderRadius: 20, textTransform: "none", fontWeight: 700 }}>
          {loading ? <CircularProgress size={18} /> : t("messages.add_btn")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── MembersPanel ──────────────────────────────────────────────────────────────

function MembersPanel({ conversationId, isAdmin, onAddMember }) {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchJson(`messages/groups/${conversationId}/members`);
      setMembers(res?.members ?? []);
    } catch { setMembers([]); }
    finally { setLoading(false); }
  }, [conversationId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.8 }}>
          {t("messages.members_label")} ({members.length})
        </Typography>
        {isAdmin && (
          <Tooltip title={t("messages.add_member_tooltip")}>
            <IconButton size="small" onClick={onAddMember}>
              <PersonAddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {loading ? (
        <CircularProgress size={16} />
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {members.map((m) => (
            <Chip
              key={m.id}
              size="small"
              label={[m.nome, m.cognome].filter(Boolean).join(" ") || m.email}
              icon={m.role === "admin"
                ? <GroupsIcon sx={{ fontSize: "14px !important" }} />
                : <PersonIcon sx={{ fontSize: "14px !important" }} />}
              variant={m.role === "admin" ? "filled" : "outlined"}
              sx={{ fontSize: 11 }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── MessagesPage (main) ───────────────────────────────────────────────────────

export default function MessagesPage({ currentUser }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "it";
  const myId = Number(currentUser?.id);

  const [sp] = useSearchParams();
  const cidFromUrl = sp.get("cid");

  const [loadingInbox, setLoadingInbox] = useState(true);
  const firstLoadDone = React.useRef(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [inbox, setInbox] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");

  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);

  const threadEndRef = useRef(null);
  const forcedCidRef = useRef(null);
  // Aggiorna il ref ogni volta che cambia cidFromUrl
  useEffect(() => { if (cidFromUrl) forcedCidRef.current = String(cidFromUrl); }, [cidFromUrl]);

  const totalUnread = useMemo(
    () => inbox.reduce((acc, c) => acc + Number(c.unread_count || 0), 0),
    [inbox]
  );

  const selected = useMemo(
    () => inbox.find((c) => String(c.id) === String(selectedId)) || null,
    [inbox, selectedId]
  );

  const isAdmin = useMemo(() => {
    if (!selected?.is_group) return false;
    return selected?.my_role === "admin";
  }, [selected]);

  // ── loaders ──

  const loadInbox = useCallback(async (opts = { keepSelection: true }) => {
    if (!firstLoadDone.current) setLoadingInbox(true);
    try {
      const res = await fetchJson("messages/inbox");
      const items = Array.isArray(res?.items) ? res.items : [];
      setInbox(items);

      if (!opts.keepSelection) return;
      // Se c'è un cid da URL, non sovrascrivere mai con il fallback
      if (cidFromUrl) return;
      // Seleziona la prima conversazione solo se non ce n'è già una attiva
      setSelectedId(prev => {
        if (prev) return prev; // mantieni sempre la selezione corrente
        return items.length ? String(items[0].id) : null;
      });
    } catch (e) {
      console.error("[MESSAGES] inbox error", e);
    } finally {
      setLoadingInbox(false);
      firstLoadDone.current = true;
    }
  }, [cidFromUrl]);

  const loadThread = useCallback(async (conversationId) => {
    if (!conversationId) return;
    setLoadingThread(true);
    try {
      const res = await fetchJson(`messages/conversations/${conversationId}?limit=50`);
      setThread(Array.isArray(res?.items) ? res.items : []);
      try { await fetchJson(`messages/conversations/${conversationId}/read`, { method: "POST" }); } catch { }
      loadInbox({ keepSelection: true });
      setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: "auto" }), 50);
    } catch (e) {
      console.error("[MESSAGES] thread error", e);
    } finally {
      setLoadingThread(false);
    }
  }, []);  // eslint-disable-line

  const onSelectConversation = (id) => {
    setSelectedId(String(id));
    setInbox((prev) =>
      prev.map((c) => (String(c.id) === String(id) ? { ...c, unread_count: 0 } : c))
    );
  };

  const onSend = async () => {
    const body = String(text || "").trim();
    if (!selectedId || !body) return;
    setText("");

    setInbox((prev) => {
      const now = new Date().toISOString();
      const updated = prev.map((c) =>
        String(c.id) === String(selectedId)
          ? { ...c, last_body: body, last_created_at: now, unread_count: 0 }
          : c
      );
      updated.sort((a, b) => new Date(b.last_created_at || 0) - new Date(a.last_created_at || 0));
      return [...updated];
    });

    try {
      await fetchJson(`messages/conversations/${selectedId}`, {
        method: "POST",
        body: JSON.stringify({ body }),
      });
      await loadThread(selectedId);
    } catch (e) {
      console.error("[MESSAGES] send error", e);
      setText(body);
    }
  };

  // ── effects ──

  useEffect(() => {
    loadInbox({ keepSelection: true });
    const t = setInterval(() => {
      if (forcedCidRef.current) return; // non fare polling se c'è un cid forzato
      loadInbox({ keepSelection: true });
    }, 12000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  // Apri direttamente la conversazione se arriva ?cid= dall'URL
  useEffect(() => {
    if (!cidFromUrl) return;
    setSelectedId(String(cidFromUrl));
    window.history.replaceState({}, "", "/messages");
    // Ricarica inbox così la conversazione appena creata compare nella lista
    loadInbox({ keepSelection: false });
  }, [cidFromUrl]); // eslint-disable-line

  useEffect(() => {
    if (!selectedId) return;
    loadThread(selectedId);
  }, [selectedId]); // eslint-disable-line

  // ── group created callback ──
  const handleGroupCreated = (newId) => {
    loadInbox({ keepSelection: false });
    setSelectedId(String(newId));
  };

  // ── render ──
  return (
    <Box sx={{ p: 2 }}>
      {/* header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>{t("messages.title")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("messages.inbox_subtitle").replace("{count}", inbox.length).replace("{unread}", totalUnread)}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<GroupAddIcon />}
            variant="contained"
            size="small"
            onClick={() => setCreateGroupOpen(true)}
            sx={{ borderRadius: 20, textTransform: "none", fontWeight: 700 }}
          >
            {t("messages.new_group_btn")}
          </Button>
          <IconButton onClick={() => loadInbox({ keepSelection: true })} title={t("messages.refresh_tooltip")}>
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Stack>

      {/* layout */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "360px 1fr" },
        gap: 2,
        alignItems: "stretch",
      }}>

        {/* ── INBOX ── */}
        <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 2 }}>
          <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t("messages.inbox_label")}</Typography>
          </Box>

          {loadingInbox ? (
            <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">{t("messages.inbox_loading")}</Typography>
            </Box>
          ) : inbox.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">{t("messages.inbox_empty")}</Typography>
            </Box>
          ) : (
            <List disablePadding sx={{ p: 1 }}>
              {inbox.map((c) => {
                const isSel = String(c.id) === String(selectedId);
                const unread = Number(c.unread_count || 0);
                const name = displayName(c);
                const preview = c.last_body ? c.last_body.slice(0, 60) : t("messages.no_message_preview");

                return (
                  <ListItemButton
                    key={c.id}
                    selected={isSel}
                    onClick={() => onSelectConversation(c.id)}
                    sx={{ borderRadius: 2, mb: 0.75, bgcolor: unread > 0 && !isSel ? "action.hover" : undefined }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ width: "100%" }}>
                      {/* avatar */}
                      <Avatar sx={{ width: 36, height: 36, fontSize: 14, bgcolor: c.is_group ? "primary.main" : "secondary.main", flexShrink: 0 }}>
                        {c.is_group ? <GroupsIcon fontSize="small" /> : (name[0] || "?").toUpperCase()}
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
                          <Typography sx={{ fontWeight: unread > 0 ? 800 : 700, fontSize: 14 }} noWrap>
                            {name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                            {timeLabel(c.last_created_at, lang)}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.25, gap: 1 }}>
                          <Typography variant="body2" noWrap sx={{
                            color: unread > 0 ? "text.primary" : "text.secondary",
                            fontWeight: unread > 0 ? 700 : 400,
                          }}>
                            {preview}
                          </Typography>

                          {unread > 0 && (
                            <Box sx={{
                              minWidth: 20, height: 20, px: 0.75, borderRadius: 999,
                              bgcolor: "error.main", color: "error.contrastText",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 11, fontWeight: 800, flexShrink: 0,
                            }}>
                              {unread}
                            </Box>
                          )}
                        </Box>

                        {c.is_group && (
                          <Chip label={t("messages.group_chip")} size="small" sx={{ mt: 0.5, height: 16, fontSize: 10, fontWeight: 700 }} />
                        )}
                      </Box>
                    </Stack>
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Paper>

        {/* ── THREAD ── */}
        <Paper variant="outlined" sx={{ borderRadius: 2, display: "flex", flexDirection: "column", minHeight: 520 }}>

          {/* thread header */}
          <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}>
            {selected ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: selected.is_group ? "primary.main" : "secondary.main" }}>
                  {selected.is_group ? <GroupsIcon sx={{ fontSize: 16 }} /> : (displayName(selected)[0] || "?").toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {displayName(selected)}
                  </Typography>
                  {selected.is_group && selected.production_name && (
                    <Typography variant="caption" color="text.secondary">
                      {selected.production_name}
                    </Typography>
                  )}
                </Box>
              </Stack>
            ) : (
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t("messages.select_conversation")}</Typography>
            )}
          </Box>

          {/* members panel for groups */}
          {selected?.is_group && (
            <MembersPanel
              conversationId={selectedId}
              isAdmin={isAdmin}
              onAddMember={() => setAddMemberOpen(true)}
            />
          )}

          {/* messages area */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2 }}>
            {loadingThread ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">{t("messages.thread_loading")}</Typography>
              </Box>
            ) : thread.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{t("messages.thread_empty")}</Typography>
            ) : (
              <Stack spacing={1.25}>
                {thread.map((m) => {
                  const isMine = Number(m.sender_user_id) === myId;
                  return (
                    <Box key={m.id} sx={{ display: "flex", justifyContent: isMine ? "flex-end" : "flex-start" }}>
                      <Box sx={{ maxWidth: "78%" }}>
                        {selected?.is_group && !isMine && m.sender_nome && (
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.main", ml: 1 }}>
                            {[m.sender_nome, m.sender_cognome].filter(Boolean).join(" ")}
                          </Typography>
                        )}
                        <Paper variant="outlined" sx={{
                          px: 2.5, py: 1.75, borderRadius: 3, borderColor: "divider",
                          bgcolor: isMine ? "action.selected" : "background.paper",
                        }}>
                          <Typography sx={{ whiteSpace: "pre-wrap", lineHeight: 1.35 }}>{m.body}</Typography>
                          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(m.created_at).toLocaleTimeString(lang === "en" ? "en-GB" : "it-IT", { hour: "2-digit", minute: "2-digit" })}
                              {isMine ? " ✓" : ""}
                            </Typography>
                          </Box>
                        </Paper>
                      </Box>
                    </Box>
                  );
                })}
                <div ref={threadEndRef} />
              </Stack>
            )}
          </Box>

          {/* composer */}
          <Box sx={{ p: 1.25, borderTop: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                fullWidth size="small"
                placeholder={selected ? t("messages.composer_placeholder") : t("messages.composer_placeholder_empty")}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); }
                }}
                disabled={!selectedId}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 999 } }}
              />
              <IconButton color="primary" onClick={onSend}
                disabled={!selectedId || !text.trim()}
                sx={{ width: 44, height: 44, borderRadius: "50%" }}>
                <SendIcon />
              </IconButton>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t("messages.composer_hint")}
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* dialogs */}
      <CreateGroupDialog
        open={createGroupOpen}
        onClose={() => setCreateGroupOpen(false)}
        onCreated={handleGroupCreated}
      />
      <AddMemberDialog
        open={addMemberOpen}
        conversationId={selectedId}
        onClose={(refreshed) => {
          setAddMemberOpen(false);
        }}
      />
    </Box>
  );
}